/**
 * Bündelt alle Domänenobjekte zu Bürgschaften.
 * Partner-Typen (Auftraggeber, Auftragnehmer etc.) leben in Partner.ts
 */
import i18n from '@/lang/i18nPlugin';
import { createEmptyPreAnnouncement, type Postadresse, type PreAnnouncement } from './Partner';
import { createEmptyPostadresse } from './Partner';
import type { Baurecht } from './DokumentBGT';
import {
  type Ansprechpartner,
  type AuftraggeberTyp,
  type BuergschaftsLieferkanal,
  type Bürgschaftsart as Buergschaftsart,
  createEmptyAnsprechpartner,
} from './Bürgschaft';
import type { ZweitprueferEntscheidung } from '@/auftraggeber/types/Auftrag.ts';

export enum Lieferkanal {
  TRUSTLOG = 'TRUSTLOG',
  GASTLOG = 'GASTLOG',
  PDF = 'PDF',
  POST = 'POST',
}

export enum Buergschaftsauftragstatus {
  UNVOLLSTAENDIG = 'UNVOLLSTAENDIG',
  IN_PRUEFUNG = 'IN_PRUEFUNG',
  ZWEITPRUEFUNG_AN = 'ZWEITPRUEFUNG_AN',
  SCHWEBE = 'SCHWEBE',
  AUSGESTELLT = 'AUSGESTELLT',
  ABGELEHNT = 'ABGELEHNT',
  STORNIERT = 'STORNIERT',
}

export enum BuergschaftsauftragstatusFilter {
  UNVOLLSTAENDIG = 'UNVOLLSTAENDIG',
  SCHWEBE = 'SCHWEBE',
  IN_PRUEFUNG = 'IN_PRUEFUNG',
  ZWEITPRUEFUNG_AN = 'ZWEITPRUEFUNG_AN',
  AUSGESTELLT = 'AUSGESTELLT',
  ABGELEHNT = 'ABGELEHNT',
  STORNIERT = 'STORNIERT',
}
export const BuergschaftsauftragStatusAnzeigetextTabelleAgTlMap: Map<
  Buergschaftsauftragstatus,
  string
> = new Map([
  [
    Buergschaftsauftragstatus.STORNIERT,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[0]'),
  ],
  [
    Buergschaftsauftragstatus.AUSGESTELLT,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[1]'),
  ],
  [
    Buergschaftsauftragstatus.ABGELEHNT,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[2]'),
  ],
  [
    Buergschaftsauftragstatus.UNVOLLSTAENDIG,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[3]'),
  ],
  [
    Buergschaftsauftragstatus.SCHWEBE,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleAgTlMap[0]'),
  ],
  [
    Buergschaftsauftragstatus.IN_PRUEFUNG,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleAgTlMap[1]'),
  ],
  [
    Buergschaftsauftragstatus.ZWEITPRUEFUNG_AN,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[6]'),
  ],
]);

export const BuergschaftsauftragStatusAnzeigetextTabelleBürgeMap: Map<
  Buergschaftsauftragstatus,
  string
> = new Map([
  [
    Buergschaftsauftragstatus.STORNIERT,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[0]'),
  ],
  [
    Buergschaftsauftragstatus.AUSGESTELLT,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[1]'),
  ],
  [
    Buergschaftsauftragstatus.ABGELEHNT,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[2]'),
  ],
  [
    Buergschaftsauftragstatus.UNVOLLSTAENDIG,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[3]'),
  ],
  [
    Buergschaftsauftragstatus.SCHWEBE,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[4]'),
  ],
  [
    Buergschaftsauftragstatus.IN_PRUEFUNG,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[5]'),
  ],
  [
    Buergschaftsauftragstatus.ZWEITPRUEFUNG_AN,
    i18n.global.t('shared.auftragStatusAnzeigetextTabelleMap[6]'),
  ],
]);

export const LieferkanalMap: Map<Lieferkanal, string> = new Map([
  [Lieferkanal.TRUSTLOG, i18n.global.t('shared.lieferkanalMap.trustlog')],
  [Lieferkanal.GASTLOG, i18n.global.t('shared.lieferkanalMap.gastlog')],
  [Lieferkanal.PDF, i18n.global.t('shared.lieferkanalMap.pdf')],
  [Lieferkanal.POST, i18n.global.t('shared.lieferkanalMap.post')],
]);

export interface Auftragsdaten {
  auftragsId?: number;
  status: string;
  art: string;
  alteBuergschaftsIdBuerge: string;
  eingangszeitpunkt: string;
  lieferkanal?: string;
}

/** @deprecated — Felder aus dem Backend, noch nicht migriert */
export interface Buergschaftsdaten {
  voraussichtlichesAblaufdatum: string;
  buergschaftsart: Buergschaftsart;
  befristungsdatum?: string;
  /** @deprecated Backend liefert camelCase, bitte befristungsdatum verwenden */
  befristungsDatum?: string;
  befristung?: boolean;
  buergschaftsBetrag: string;
  waehrungskennzeichen?: string;
  vertragsnummer: string;
  vertragsdatum: string;
  bauleistung: string;
  bauvorhaben?: Bauvorhaben;
  buergschaftsauftragstatus?: Buergschaftsauftragstatus;
  baurecht?: Baurecht;
  bausumme?: string;
  bausummeProzent?: string;
  buergschaftsId?: string;
  buergschaftsIdBuerge?: string;
  internerVermerk?: string;
  ausstellungsdatumBuerge?: string;
  zahlungAufErstesAnfordern?: boolean;
}

export interface BuergschaftsauftragMitteilungDetails {
  buergeId: string;
  buergeName: string;
  letzerPruefer?: string;
  status?: string;
  lieferkanal?: BuergschaftsLieferkanal;
  eingangszeitpunkt: string;
  auftraggeberName?: string;
  postadresseAuftraggeber?: Postadresse;
  auftragnehmerName?: string;
  postadresseAuftragnehmer?: Postadresse;
  bauleistung?: string;
  bauadresse?: Postadresse;
  bauvorhaben: string;
  buergschaftId?: string;
  buergschaftsart?: Buergschaftsart;
  buergschaftsBetrag?: number;
}

export interface BuergschaftsauftragDetailsFuerAufgabenListe {
  buergschaftsauftragId?: number;
  status?: string;
}

export interface GuaranteeOrderHistoryEntry {
  erstelltVon: string;
  geaendertAm: Date;
  status: string;
  beschreibung?: string;
  zweitpruefungFreigabe: boolean;
}

export interface HistoryDescription {
  text: string;
  linkText?: string;
  afterLinkText?: string;
  onLinkClick?: () => void;
}

/** Der Auftragnehmer ist der Schuldner im Bürgschaftsvertrag */
export interface Auftragnehmer {
  auftragnehmerId?: string;
  fremdId: string;
  name: string;
  postadresse: Postadresse;
  anzahlToechter?: number;
  ansprechpartner?: Ansprechpartner;
}

export interface BuergeDaten {
  ablehnungsgrund?: string;
  ansprechpartner?: Ansprechpartner;
}

/** Das Bauvorhaben beschreibt den Ort der zu erbringenden Leistung */
export interface Bauvorhaben {
  adresse?: Postadresse;
  freitext?: string;
}

/** Der Auftraggeber ist der Gläubiger im Bürgschaftsvertrag */
export interface Auftraggeber {
  auftraggeberId: string | null;
  name: string;
  postadresse: Postadresse;
  versandadresse?: Versandadresse | null;
  oeffentlicherAuftraggeber?: boolean;
  vertretungsformel?: string[];
  partnertyp: AuftraggeberTyp | null;
  ansprechpartner?: Ansprechpartner | null;
  preAnnouncement?: PreAnnouncement;
}

/** Die Versandadresse für Papierbürgschaften */
export interface Versandadresse {
  name: string;
  zustellanweisung: string;
  postadresse: Postadresse;
}

/** Der Bürge der Bürgschaft */
export interface Buerge {
  id: string;
}

/** Die Template-ID und das Recht der Bürgschaft */
export interface Template {
  buergschaftstemplateId: string;
  recht?: Baurecht;
}

export interface Buergschaftsauftrag {
  auftrag?: Auftragsdaten;
  eingangszeitpunkt?: string;
  buergschaftsdaten: Buergschaftsdaten;
  auftragnehmer: Auftragnehmer;
  auftraggeber: Auftraggeber;
  buergeId?: string;
  buergschaftstext?: Template;
}

export interface BuergschaftsauftragBeantragen {
  buergenId: string;
  auftrag: {
    art: string;
    alteBuergschaftsIdBuerge: string;
    lieferkanal: Lieferkanal;
  };
  auftraggeber: Auftraggeber | null;
  auftragnehmer: Auftragnehmer | null;
  ansprechpartner?: Ansprechpartner;
  buergschaft: {
    vertragsdatum: string;
    vertragsnummer: string;
    buergschaftsart: Buergschaftsart;
    befristungsDatum: string;
    buergschaftsBetrag: number;
    bausumme: number | undefined;
    bausummeProzent: number | undefined;
    internerVermerk: string;
    zahlungAufErstesAnfordern: boolean;
    bauleistung: string;
    bauvorhaben: {
      freitext: string;
      adresse: Postadresse;
    };
    voraussichtlichesAblaufdatum: string | null;
  };
  buergschaftstext: {
    buergschaftstemplateId: string;
    recht: Baurecht;
  };
  zahlungAufErstesAnfordernRechtstextID: number | null;
  zweitprueferEntscheidung: ZweitprueferEntscheidung;
}

export type BuergschaftsauftragBeantragenDto = Omit<
  BuergschaftsauftragBeantragen,
  'buergschaft' | 'ansprechpartner' | 'zweitprueferEntscheidung'
> & {
  buergschaft: Omit<
    BuergschaftsauftragBeantragen['buergschaft'],
    'buergschaftsBetrag' | 'bausumme'
  > & {
    buergschaftsBetrag: string;
    bausumme: string | undefined;
  };
  zweitprueferEntscheidung: Omit<
    BuergschaftsauftragBeantragen['zweitprueferEntscheidung'],
    'zweitpruefer'
  > & {
    nutzerId: string;
  };
};

export function createEmptyVersandadresse(): Versandadresse {
  return {
    name: '',
    zustellanweisung: '',
    postadresse: createEmptyPostadresse(),
  };
}

export function createEmptyAuftraggeber(): Auftraggeber {
  return {
    auftraggeberId: null,
    name: '',
    postadresse: createEmptyPostadresse(),
    versandadresse: createEmptyVersandadresse(),
    oeffentlicherAuftraggeber: false,
    vertretungsformel: [],
    partnertyp: null,
    preAnnouncement: createEmptyPreAnnouncement(),
  };
}

export function createEmptyAuftragnehmer(): Auftragnehmer {
  return {
    auftragnehmerId: null,
    fremdId: '',
    name: '',
    postadresse: createEmptyPostadresse(),
    ansprechpartner: createEmptyAnsprechpartner(),
  };
}

export function createEmptyBuerge(): Buerge {
  return {
    id: '',
  };
}

export function createEmptyBuergschaftsauftragBeantragen(): BuergschaftsauftragBeantragen {
  return {
    buergenId: '',
    auftrag: {
      art: 'NEUANTRAG',
      alteBuergschaftsIdBuerge: '',
      lieferkanal: undefined,
    },
    auftraggeber: null,
    auftragnehmer: null,
    buergschaft: {
      vertragsdatum: '',
      vertragsnummer: '',
      buergschaftsart: undefined,
      befristungsDatum: undefined,
      buergschaftsBetrag: undefined,
      bausumme: undefined,
      internerVermerk: '',
      bausummeProzent: undefined,
      zahlungAufErstesAnfordern: false,
      bauleistung: '',
      bauvorhaben: {
        freitext: undefined,
        adresse: createEmptyPostadresse(),
      },
      voraussichtlichesAblaufdatum: undefined,
    },
    buergschaftstext: {
      buergschaftstemplateId: '',
      recht: undefined,
    },
    zahlungAufErstesAnfordernRechtstextID: null,
    zweitprueferEntscheidung: null,
  };
}

export function createEmptyBuergschaftsdaten(): Buergschaftsdaten {
  return {
    voraussichtlichesAblaufdatum: '',
    buergschaftsBetrag: '',
    buergschaftsart: undefined,
    vertragsdatum: '',
    vertragsnummer: '',
    befristungsdatum: '',
    bauleistung: '',
    bauvorhaben: {
      adresse: createEmptyPostadresse(),
    },
    ausstellungsdatumBuerge: '',
    zahlungAufErstesAnfordern: false,
  };
}

export function createEmptyBuergschaftsauftrag(): Buergschaftsauftrag {
  return {
    auftrag: null,
    eingangszeitpunkt: null,
    buergschaftsdaten: createEmptyBuergschaftsdaten(),
    auftragnehmer: createEmptyAuftragnehmer(),
    auftraggeber: createEmptyAuftraggeber(),
    buergeId: '',
    buergschaftstext: null,
  };
}

export function createEmptyBuergendaten(): BuergeDaten {
  return {
    ablehnungsgrund: '',
    ansprechpartner: createEmptyAnsprechpartner(),
  };
}

/** Die Template-ID der Bürgschaft */
export interface Auftragsdaten {
  auftragsId?: number;
  status: string;
  art: string;
  alteBuergschaftsIdBuerge: string;
  eingangszeitpunkt: string;
  lieferkanal?: string;
}

export function mappeBuergschaftsdaten(
  buergschaftsdaten: Buergschaftsdaten,
): Record<string, unknown> {
  return {
    voraussichtlichesAblaufdatum: buergschaftsdaten.voraussichtlichesAblaufdatum,
    buergschaftsart: buergschaftsdaten.buergschaftsart,
    befristungsdatum: buergschaftsdaten.befristungsdatum ?? buergschaftsdaten.befristungsDatum,
    befristung: buergschaftsdaten.befristung,
    waehrungskennzeichen: buergschaftsdaten.waehrungskennzeichen,
    vertragsnummer: buergschaftsdaten.vertragsnummer,
    vertragsdatum: buergschaftsdaten.vertragsdatum,
    bauleistung: buergschaftsdaten.bauleistung,
    bauvorhaben: buergschaftsdaten.bauvorhaben,
    buergschaftsauftragstatus: buergschaftsdaten.buergschaftsauftragstatus,
    baurecht: buergschaftsdaten.baurecht,
    bausumme: buergschaftsdaten.bausumme,
    bausummeprozent: buergschaftsdaten.bausummeProzent,
    internerVermerk: buergschaftsdaten.internerVermerk,
    ausstellungsdatumBuerge: buergschaftsdaten.ausstellungsdatumBuerge,
    buergschaftsbetrag: buergschaftsdaten.buergschaftsBetrag,
    buergschaftId: buergschaftsdaten.buergschaftsId,
    buergschaftIdBuerge: buergschaftsdaten.buergschaftsIdBuerge,
  };
}
